import { ArchivingJobExperimentStatusDto } from 'business/models/Models';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { FmlxButton } from 'fmlx-common-ui';
import React, { useMemo } from 'react';
import { useRMDispatch } from 'redux/hooks';
import { useSelectUnarchivingResults } from 'redux/hooks/unarchiving';
import { clearUnarchivingResults } from 'redux/reducers/unarchivingReducer';
import { dismissArchivingResultsThunk } from 'redux/thunks/archiving';
import { showAlertSnackBar } from 'utils/Global';
import { CustomTable } from '../controls/customTable';
import { UnarchiveResultsColumns } from './constants';
import { getUnarchiveStatusCell } from './unarchiveStatusCell';
import { getExperimentRoutePath } from './utils';
import { isDismissableArchivingResultStatus } from '../utils';

const formatDate = (value?: Date) => value?.toLocaleDateString('en-US');

export const UnarchiveResultsTable = () => {
  const dispatch = useRMDispatch();
  const results = useSelectUnarchivingResults();

  const data = useMemo(
    () =>
      results.map((row) => ({
        name:
          row.status === ArchivingJobExperimentStatusDto.Completed &&
          row.newExperimentId ? (
            <a
              href={getExperimentRoutePath(row.newExperimentId)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {row.experimentName}
            </a>
          ) : (
            row.experimentName
          ),
        fileLocation: row.currentLocation,
        dateCreated: formatDate(row.dateCreated),
        lastInspectionDate: formatDate(row.inspectionDate),
        dateRestored: formatDate(row.restoredDate),
        status: getUnarchiveStatusCell(row.status, row.errorMessage),
      })),
    [results],
  );

  const handleClearResults = async () => {
    const dismissableExperimentIds = results
      .filter((row) => isDismissableArchivingResultStatus(row.status))
      .map((row) => row.experimentId);

    if (dismissableExperimentIds.length === 0) {
      dispatch(clearUnarchivingResults());
      return;
    }

    try {
      await dispatch(dismissArchivingResultsThunk(dismissableExperimentIds)).unwrap();
      dispatch(clearUnarchivingResults());
    } catch (error) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        typeof error === 'string' ? error : 'Failed to clear unarchiving results.',
      );
    }
  };

  return (
    <div
      className="tab-content-height w-full flex flex-col"
      data-testid="unarchive-results-table"
    >
      <div className="flex items-center justify-between border-b border-gray-300 bg-white px-4 py-2">
        <div className="flex items-center gap-8 text-sm text-gray-700">
          <span>
            <span className="text-gray-500">Unarchived:</span>{' '}
            <span className="font-semibold text-gray-900">{data.length}</span>
          </span>
        </div>
        <FmlxButton
          label="Clear Results"
          size="sm"
          type={FmlxButton.Type.BASIC}
          disabled={data.length === 0}
          onClick={handleClearResults}
        />
      </div>
      <div className="flex-1 w-full min-h-0">
        <CustomTable
          columns={UnarchiveResultsColumns}
          data={data}
          enableSelection={false}
          id="unarchive-results-table"
        />
      </div>
    </div>
  );
};
